<template>
  <div class="flex flex-col h-full min-h-0">
    <div
      v-if="isAdmin"
      class="flex justify-end px-3 py-1 border-b border-white/5"
    >
      <button
        type="button"
        class="flex items-center gap-1 text-[11px] text-text-dim hover:text-red-400 transition-colors"
        :disabled="clearing"
        @click="onClearChat"
      >
        <el-icon size="12"><Delete /></el-icon>
        <span>{{ $t('chat.admin.clear_chat') }}</span>
      </button>
    </div>

    <!-- Pinned banner — admins can pin one message in the global thread.
         Click jumps to the message; the X unpins (admin-only). -->
    <ChatPinnedBanner
      v-if="pinnedMessageId"
      :thread-id="globalThreadId || ''"
      :pinned-message-id="pinnedMessageId"
      :snippet="pinnedSnippet"
      :can-unpin="isAdmin"
      @jump="onJumpTo"
    />

    <ChatMessageList
      ref="messageListRef"
      class="flex-1 min-h-0"
      :messages="messages"
      :loading="loading"
      :loading-more="loadingMore"
      :has-more="hasMore"
      :can-pin-messages="isAdmin"
      :empty-label="$t('chat.global.empty')"
      @retry="onRetry"
      @load-older="loadOlder"
      @delete="onDelete"
      @mute="onMute"
      @reply="onReplyTo"
      @pin="onPinMessage"
      @jump-to="onJumpTo"
    />

    <ChatTypingIndicator thread-id="global" :participants="new Map()" />

    <ChatComposer
      :sending="sending"
      thread-id="global"
      thread-kind="global"
      :reply-to="replyTo"
      :author-name="myDisplayName"
      :placeholder="$t('chat.global.placeholder')"
      @send="onSend"
      @cancel-reply="replyTo = null"
    />

    <ChatAdminMuteDialog
      v-model="muteDialogOpen"
      :user-id="muteTarget?.userId || null"
      :user-name="muteTarget?.userName || ''"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessageBox, ElMessage } from 'element-plus'
import { Delete } from '@element-plus/icons-vue'
import { useGlobalChat } from '~/composables/chat/useGlobalChat'
import { useChatModeration } from '~/composables/chat/useChatModeration'
import { useChatTranslation } from '~/composables/chat/useChatTranslation'

const { t } = useI18n()
const { user } = useUserSession()
const {
  messages, loading, loadingMore, hasMore, sending, threadId: globalThreadId,
  loadInitial, loadOlder, sendText, retrySend, inited,
  pinnedMessageId
} = useGlobalChat()
const { deleteMessage } = useChatModeration()
const { hydrateBatch } = useChatTranslation()

const muteDialogOpen = ref(false)
const muteTarget = ref(null)
const clearing = ref(false)
const messageListRef = ref(null)

const isAdmin = computed(() => user.value?.role === 'admin')
const myDisplayName = computed(() => {
  const u = user.value || {}
  return u.chatProfile?.displayName || u.name || ''
})

// Quote-reply state — set by clicking Reply on a message; consumed by
// sendText below and cleared after submit / cancel.
const replyTo = ref(null)
function onReplyTo(msg) {
  replyTo.value = {
    id: msg.id,
    authorName: msg.author?.name || 'User',
    bodySnippet: (msg.body || '').slice(0, 120)
  }
}

// Pinned snippet — pulled from the local messages array. Falls back to
// a placeholder when the pinned message is out of the loaded window
// (older messages not yet fetched). Click still attempts to scroll.
const pinnedSnippet = computed(() => {
  const id = pinnedMessageId.value
  if (!id) return ''
  const m = (messages.value || []).find((x) => x.id === id)
  if (!m) return 'Open pinned message…'
  if (m.deletedAt) return '(message deleted)'
  return (m.body || '').slice(0, 120) || (m.attachments?.length ? '📎 Attachment' : '—')
})

async function onPinMessage(messageId) {
  if (!globalThreadId.value) return
  try {
    await $fetch(`/api/chat/threads/${globalThreadId.value}/pin`, {
      method: 'POST',
      body: { messageId }
    })
  } catch (err) {
    console.error('[chat:global pin] failed', err)
  }
}

function onJumpTo(messageId) {
  messageListRef.value?.scrollToMessage?.(messageId)
}

function onDelete(messageId) { deleteMessage(messageId) }
function onMute(info) { muteTarget.value = info; muteDialogOpen.value = true }

async function onClearChat() {
  try {
    await ElMessageBox.confirm(
      t('chat.admin.clear_confirm_body'),
      t('chat.admin.clear_confirm_title'),
      {
        confirmButtonText: t('chat.admin.clear_confirm_ok'),
        cancelButtonText: t('chat.settings.cancel'),
        type: 'warning'
      }
    )
  } catch {
    // user cancelled
    return
  }

  clearing.value = true
  try {
    // No success toast — policy is "no visible indication that anything
    // was cleared". The chat just empties out (via the `chat.global.cleared`
    // WS frame the server publishes), which is feedback enough for the
    // admin who triggered the action.
    await $fetch('/api/chat/admin/global/clear', { method: 'POST' })
  } catch (err) {
    // Error toast still surfaces — the admin needs to know if the request
    // failed (otherwise they can't tell silent-success from silent-failure).
    console.error('[chat:admin] clear failed', err)
    ElMessage.error(t('chat.admin.clear_failed'))
  } finally {
    clearing.value = false
  }
}

onMounted(async () => {
  if (!inited()) await loadInitial()
  // Prime the translation cache for the whole initial page in one batch
  // call per chunk. ChatMessageItem's per-message watcher will see the
  // pending state and not fire individual requests.
  const target = user.value?.chatProfile?.preferredLanguage || null
  const myId = String(user.value?.id || user.value?._id || '')
  if (target) hydrateBatch(messages.value, target, myId)
})

function currentUserPublic() {
  const u = user.value || {}
  return {
    id: String(u.id || u._id || ''),
    name: u.chatProfile?.displayName || u.name || 'User',
    avatar: u.chatProfile?.avatarUrl || u.spotify?.imageUrl || u.soundcloud?.imageUrl || u.avatar || null,
    role: u.role || 'customer'
  }
}

function onSend(payload) {
  // Composer now emits { body, attachments, replyTo } — support legacy string form too
  if (typeof payload === 'string') {
    sendText(payload, currentUserPublic())
  } else {
    sendText(payload.body, currentUserPublic(), payload.attachments || [], payload.replyTo || null)
  }
  replyTo.value = null
}

function onRetry(clientMsgId) {
  retrySend(clientMsgId, currentUserPublic())
}
</script>
